
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addWeeks, startOfWeek, endOfWeek } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useLanguage } from '@/contexts/LanguageContext';

interface WeekSelectorProps {
  selectedWeek: Date;
  onSelectWeek: (date: Date) => void;
}

export const WeekSelector: React.FC<WeekSelectorProps> = ({ 
  selectedWeek, 
  onSelectWeek 
}) => {
  const { t } = useLanguage();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayedWeek, setDisplayedWeek] = useState(selectedWeek);
  const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Update displayed week when selected week changes (from external sources)
  useEffect(() => {
    if (!isTransitioning) {
      setDisplayedWeek(selectedWeek);
    }
  }, [selectedWeek, isTransitioning]);
  
  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);
  
  const weekStart = startOfWeek(displayedWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(displayedWeek, { weekStartsOn: 1 });
  
  const handlePreviousWeek = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    
    transitionTimeoutRef.current = setTimeout(() => {
      const newDate = addWeeks(selectedWeek, -1);
      onSelectWeek(newDate);
      
      // Wait a short time then start showing the next content with a fade-in
      setTimeout(() => {
        setDisplayedWeek(newDate);
        setIsTransitioning(false);
      }, 150);
    }, 200);
  };
  
  const handleNextWeek = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    
    transitionTimeoutRef.current = setTimeout(() => {
      const newDate = addWeeks(selectedWeek, 1);
      onSelectWeek(newDate);
      
      // Wait a short time then start showing the next content with a fade-in
      setTimeout(() => {
        setDisplayedWeek(newDate);
        setIsTransitioning(false);
      }, 150);
    }, 200);
  };
  
  const handleDateSelect = (date: Date | undefined) => {
    if (date && !isTransitioning) {
      setIsTransitioning(true);
      
      transitionTimeoutRef.current = setTimeout(() => {
        onSelectWeek(date);
        
        // Wait a short time then start showing the next content with a fade-in
        setTimeout(() => {
          setDisplayedWeek(date);
          setIsTransitioning(false);
        }, 150);
      }, 200);
    }
  };
  
  const isCurrentWeek = () => {
    const today = new Date();
    const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
    return weekStart.getTime() === currentWeekStart.getTime();
  };
  
  const transitionStyle = {
    transition: `opacity ${isTransitioning ? '0.2s' : '0.3s'} ease-in-out, 
                 transform ${isTransitioning ? '0.2s' : '0.3s'} ease-in-out`,
    opacity: isTransitioning ? 0 : 1,
    transform: isTransitioning ? 'translateY(5px)' : 'translateY(0)',
    willChange: 'opacity, transform'
  };
  
  return (
    <div className="flex items-center space-x-2">
      <Button
        variant="outline"
        size="icon"
        onClick={handlePreviousWeek}
        disabled={isTransitioning}
        className="transition-all duration-200 hover:bg-muted"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="flex items-center gap-1 px-3"
            size="sm"
            style={transitionStyle}
          >
            <CalendarIcon className="h-4 w-4" />
            <span>
              {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[560px] max-w-[90vw] p-0">
          <Calendar
            mode="single"
            selected={selectedWeek}
            onSelect={handleDateSelect}
            weekStartsOn={1}
          />
        </PopoverContent>
      </Popover>
      
      <Button
        variant="outline"
        size="icon"
        onClick={handleNextWeek}
        disabled={isTransitioning}
        className="transition-all duration-200 hover:bg-muted"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      
      {!isCurrentWeek() && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDateSelect(new Date())}
          disabled={isTransitioning}
          className="transition-all duration-200"
        >
          {t('currentWeek') || 'Current Week'}
        </Button>
      )}
    </div>
  );
};
